// Wallet document categorization based on filename analysis
export type WalletCategory = 'boarding-pass' | 'id-passport' | 'hotel' | 'insurance' | 'payment' | 'other';

interface CategoryPattern {
  keywords: string[];
  category: WalletCategory;
  brands?: {
    pattern: RegExp;
    name: string;
    color: string;
  }[];
}

const CATEGORY_PATTERNS: CategoryPattern[] = [
  {
    keywords: ['boarding', 'flight', 'airline', 'ticket', 'eticket', 'e-ticket'],
    category: 'boarding-pass',
    brands: [
      { pattern: /american\s*airlines?/i, name: 'American Airlines', color: 'from-blue-700 to-blue-900' },
      { pattern: /delta/i, name: 'Delta', color: 'from-red-600 to-red-800' },
      { pattern: /united/i, name: 'United', color: 'from-blue-600 to-blue-800' },
      { pattern: /southwest/i, name: 'Southwest', color: 'from-yellow-600 to-orange-700' },
      { pattern: /jetblue/i, name: 'JetBlue', color: 'from-blue-500 to-blue-700' },
      { pattern: /spirit/i, name: 'Spirit', color: 'from-yellow-500 to-yellow-700' },
      { pattern: /alaska/i, name: 'Alaska Airlines', color: 'from-teal-600 to-teal-800' },
    ]
  },
  {
    keywords: ['passport', 'id', 'identification', 'driver', 'license', 'visa'],
    category: 'id-passport',
    brands: [
      { pattern: /passport/i, name: 'Passport', color: 'from-red-700 to-red-900' },
      { pattern: /driver.*license|dmv/i, name: 'Driver License', color: 'from-blue-700 to-blue-900' },
      { pattern: /visa/i, name: 'Visa', color: 'from-purple-700 to-purple-900' },
    ]
  },
  {
    keywords: ['hotel', 'reservation', 'booking', 'accommodation', 'stay'],
    category: 'hotel',
    brands: [
      { pattern: /marriott|courtyard|residence\s*inn/i, name: 'Marriott', color: 'from-red-600 to-red-800' },
      { pattern: /hilton|hampton/i, name: 'Hilton', color: 'from-blue-600 to-blue-800' },
      { pattern: /hyatt/i, name: 'Hyatt', color: 'from-purple-600 to-purple-800' },
      { pattern: /ihg|holiday\s*inn|intercontinental/i, name: 'IHG', color: 'from-teal-600 to-teal-800' },
      { pattern: /airbnb/i, name: 'Airbnb', color: 'from-pink-600 to-red-700' },
    ]
  },
  {
    keywords: ['insurance', 'policy', 'coverage', 'claim'],
    category: 'insurance',
    brands: [
      { pattern: /geico/i, name: 'GEICO', color: 'from-green-600 to-green-800' },
      { pattern: /state\s*farm/i, name: 'State Farm', color: 'from-red-600 to-red-800' },
      { pattern: /allstate/i, name: 'Allstate', color: 'from-blue-600 to-blue-800' },
      { pattern: /progressive/i, name: 'Progressive', color: 'from-blue-500 to-blue-700' },
    ]
  },
  {
    keywords: ['card', 'credit', 'debit', 'visa', 'mastercard', 'amex', 'bank', 'payment'],
    category: 'payment',
    brands: [
      { pattern: /bank\s*of\s*america|boa/i, name: 'Bank of America', color: 'from-red-700 to-red-900' },
      { pattern: /chase/i, name: 'Chase', color: 'from-blue-700 to-blue-900' },
      { pattern: /wells\s*fargo/i, name: 'Wells Fargo', color: 'from-yellow-600 to-red-700' },
      { pattern: /citi(bank)?/i, name: 'Citi', color: 'from-blue-600 to-blue-800' },
      { pattern: /capital\s*one/i, name: 'Capital One', color: 'from-red-600 to-orange-700' },
      { pattern: /discover/i, name: 'Discover', color: 'from-orange-600 to-orange-800' },
      { pattern: /american\s*express|amex/i, name: 'American Express', color: 'from-blue-600 to-blue-800' },
      { pattern: /visa/i, name: 'Visa', color: 'from-blue-700 to-blue-900' },
      { pattern: /mastercard/i, name: 'Mastercard', color: 'from-red-600 to-orange-700' },
    ]
  }
];

export interface CategorizedWalletDocument {
  category: WalletCategory;
  brand?: string;
  brandColor?: string;
  confidence: number; // 0-1
}

export function categorizeWalletDocument(filename: string): CategorizedWalletDocument {
  const lowerFilename = filename.toLowerCase();
  
  // Check each category pattern
  for (const pattern of CATEGORY_PATTERNS) {
    // Check if filename contains any category keywords
    const hasKeyword = pattern.keywords.some(keyword => 
      lowerFilename.includes(keyword.toLowerCase())
    );
    
    if (hasKeyword) {
      // Try to identify brand
      if (pattern.brands) {
        for (const brand of pattern.brands) {
          if (brand.pattern.test(filename)) {
            return {
              category: pattern.category,
              brand: brand.name,
              brandColor: brand.color,
              confidence: 0.9
            };
          }
        }
      }
      
      // Category matched but no specific brand
      return {
        category: pattern.category,
        confidence: 0.7
      };
    }
  }
  
  // No match found
  return {
    category: 'other',
    confidence: 0.5
  };
}

// Extract potential QR code or barcode identifiers from filename
export function extractIdentifiers(filename: string): {
  confirmationNumber?: string;
  flightNumber?: string;
  recordLocator?: string;
} {
  const result: {
    confirmationNumber?: string;
    flightNumber?: string;
    recordLocator?: string;
  } = {};
  
  // Flight number patterns (e.g., AA1234, DL567)
  const flightMatch = filename.match(/\b([A-Z]{2}\d{2,4})\b/);
  if (flightMatch) {
    result.flightNumber = flightMatch[1];
  }
  
  // Confirmation/record locator (6-letter codes common in airlines)
  const confirmMatch = filename.match(/\b([A-Z]{6})\b/);
  if (confirmMatch && confirmMatch[1] !== result.flightNumber) {
    result.confirmationNumber = confirmMatch[1];
  }
  
  // Generic confirmation number patterns
  const confNumMatch = filename.match(/conf(?:irmation)?[#:\s]*([A-Z0-9]{4,})/i);
  if (confNumMatch) {
    result.recordLocator = confNumMatch[1];
  }
  
  return result;
}
